import React, { useEffect, useRef } from "react";

import { TranscriptSegment } from "../../types/calls.types";

import {
  TRANSCRIPT_SPEAKER_COLOUR,
  TRANSCRIPT_SPEAKER_FALLBACK_COLOUR,
} from "./callsExplorer.constants";

// After the user scrolls the transcript themselves, hold off auto-scrolling
// for a while so we don't fight them.
const MANUAL_SCROLL_PAUSE_MS = 4000;

const isActive = (t: number, start: number, end: number) => t >= start && t < end;

interface CallTranscriptProps {
  segments: TranscriptSegment[];
  currentTime: number;
  onSeek: (seconds: number) => void;
}

export const CallTranscript: React.FC<CallTranscriptProps> = ({
  segments,
  currentTime,
  onSeek,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastManualScroll = useRef(0);
  const autoScrolling = useRef(false);

  // Segments overlap when people talk over each other - follow the first
  // active one.
  const followIndex = segments.findIndex((seg) => isActive(currentTime, seg.start, seg.end));

  // Scroll the box only - scrollIntoView would also scroll the page, since
  // the transcript sits inside an expanded table row.
  useEffect(() => {
    const container = containerRef.current;
    if (followIndex < 0 || !container) return;
    if (Date.now() - lastManualScroll.current < MANUAL_SCROLL_PAUSE_MS) return;
    const el = container.querySelector<HTMLElement>(
      `[data-segment-index="${followIndex.toString()}"]`,
    );
    if (!el) return;
    autoScrolling.current = true;
    container.scrollTop = el.offsetTop - container.clientHeight / 2 + el.clientHeight / 2;
  }, [followIndex]);

  const handleScroll = () => {
    if (autoScrolling.current) {
      autoScrolling.current = false;
      return;
    }
    lastManualScroll.current = Date.now();
  };

  return (
    <div
      ref={containerRef}
      className="border rounded p-3 bg-white shadow-inner"
      style={{ maxHeight: "300px", overflowY: "auto", position: "relative" }}
      onScroll={handleScroll}
    >
      {segments.map((seg, i) => {
        const showLabel = i === 0 || seg.speaker !== segments[i - 1].speaker;
        const segmentActive = isActive(currentTime, seg.start, seg.end);
        // Fall back to the whole segment as one clickable "word" if word
        // timings are ever missing
        const words = seg.words.length > 0 ? seg.words : [{ ...seg, word: seg.text }];
        return (
          <React.Fragment key={`${seg.start.toString()}-${i.toString()}`}>
            {showLabel && (
              <span className="d-block mt-2 mb-1 fw-bold small text-muted">{seg.speaker}</span>
            )}
            <span
              data-segment-index={i}
              className={`d-inline-block p-1 me-1 mb-1 rounded ${segmentActive ? "segment-active-light" : ""}`}
              style={{
                backgroundColor:
                  TRANSCRIPT_SPEAKER_COLOUR[seg.speaker] ?? TRANSCRIPT_SPEAKER_FALLBACK_COLOUR,
              }}
            >
              {words.map((w, j) => (
                <React.Fragment key={j}>
                  {j > 0 && " "}
                  <span
                    className={`transcript-word rounded ${segmentActive && isActive(currentTime, w.start, w.end) ? "segment-active" : ""}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      onSeek(w.start);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSeek(w.start);
                      }
                    }}
                  >
                    {w.word}
                  </span>
                </React.Fragment>
              ))}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
};
